import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import Avatar from "../common/Avatar";
import ImageOrInitials from "../agency/ImageOrInitials";
import ClubVerifiedBadge from "./ClubVerifiedBadge";
import FlagImg from "./FlagImg";
import { BookmarkGlyph, CloseGlyph } from "./glyphs";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import {
  formatPositionValue,
  formatStaffRole,
  formatTypeOfTender,
  formatLeagueTier,
  formatWindowDateRange,
} from "../../constants/agentTenderChoices";
import { formatRange } from "../../utils/tenderFormatters";
import {
  buildFinRows,
  getCommission,
  getPeriodLine,
  getMarketValueLine,
  getGamesPlayedLine,
  getExperienceLine,
  formatHeightMeters,
  formatDurationChip,
} from "../../utils/agentRequestFigures";

// ──────────────────────────────────────────────────────────────────────────
// Read-only quick preview (loupe) for an agent request — V2 UI rebuild 1:1
// from design doc 01 ("Loupe preview · the completed information set").
// Every filled form field appears here with its REAL figure; description +
// key requirements in full.
//
// TWO artboard layouts:
//   • ≥768  — centred 960px modal near the top of the screen: main column +
//             300px rail (poster identity, track record, actions).
//   • <768  — bottom SHEET (86% height): grab handle, pill header, figures,
//             spec ROWS (not a grid), agency line, sticky action bar with
//             the square bookmark + full-width CTA.
// Portal + ESC + body-scroll-lock preserved.
//
// TWO surfaces, driven by `variant`:
//   • "feed"  → ANONYMIZED. The club is NEVER shown (Brief Rule 4), no matter
//               what the tender object happens to carry.
//   • "mine"  → the agent's own internal view: the club it belongs to is
//               shown (Rule 8 — the posting agency sees its own club).
// ──────────────────────────────────────────────────────────────────────────

const typePillClass = (type) => {
  if (type === "buy") return "bg-[#E8F1FF] text-[#1B6FE0]";
  if (type === "loan") return "bg-[#EEF2FF] text-[#4F46E5]";
  if (type === "trial") return "bg-[#FEF3C7] text-[#B45309]";
  return "bg-[#F1F5F9] text-[#334155]";
};

const CellLabel = ({ children }) => (
  <div className="text-[10.5px] font-bold tracking-[0.06em] text-[#94A3B8] uppercase">
    {children}
  </div>
);

// One cell of the desktop spec grid — hidden entirely when the value is empty.
const SpecCell = ({ label, value }) => {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div>
      <CellLabel>{label}</CellLabel>
      <div className="text-[13.5px] font-semibold text-[#0F172A] mt-0.5 break-words">
        {value}
      </div>
    </div>
  );
};

// One row of the mobile spec list (artboard sheet: bordered label/value rows).
const SpecRow = ({ label, value }) => {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex justify-between gap-3 py-[9px]">
      <span className="text-[12.5px] text-[#64748B]">{label}</span>
      <span className="text-[13px] font-semibold text-[#0F172A] text-right break-words">
        {value}
      </span>
    </div>
  );
};

// The loupe figures box: same data as the card fin-box but at reading scale,
// commission separated by a dashed rule, with the split note underneath.
// `compact` = the mobile-sheet scale from the artboard.
const LoupeFigures = ({ tender, compact = false }) => {
  const rows = buildFinRows(tender, compact ? "mo" : "month");
  const commission = getCommission(tender);
  const labelCls = compact ? "text-[12.5px]" : "text-[13px]";
  const valueCls = compact ? "text-[14px]" : "text-[15px]";
  return (
    <div
      className={`bg-[#F8FAFC] border border-[#EEF2F7] flex flex-col ${
        compact ? "rounded-[10px] px-3.5 py-3 gap-[7px]" : "rounded-[11px] px-4 py-3.5 gap-2"
      }`}
    >
      {rows.map((r) => (
        <div key={r.key} className="flex justify-between gap-3">
          <span className={`${labelCls} text-[#64748B]`}>{r.label}</span>
          <span className={`${valueCls} font-bold text-right tabular-nums text-[#0F172A]`}>
            {r.value}
          </span>
        </div>
      ))}
      {rows.length === 0 && (
        <div className={`${labelCls} text-[#475569]`}>Conditions negotiable</div>
      )}
      <div
        className={`border-t border-dashed border-[#E2E8F0] flex justify-between gap-3 ${
          compact ? "pt-[7px]" : "pt-2"
        }`}
      >
        <span className={`${labelCls} text-[#64748B]`}>Commission</span>
        {commission.state === "none" ? (
          <span
            className="text-[13px] font-semibold text-[#B45309] text-right"
            title="The posting agency won't pay or share a commission on this deal."
          >
            No commission for this job
          </span>
        ) : (
          <span
            className={`${compact ? "text-[13px]" : "text-[14px]"} font-bold ${
              commission.state === "split" ? "text-[#0F172A]" : "text-[#64748B]"
            }`}
            title={
              commission.state === "negotiable"
                ? "The split isn't fixed yet · agree it in chat when you respond."
                : undefined
            }
          >
            {commission.state === "split" ? commission.value : "Negotiable"}
          </span>
        )}
      </div>
      {commission.state === "split" && (
        <div className="text-[12px] text-[#64748B]">
          {/* The agent's own note wins; the static sentence is the fallback (F8). */}
          {commission.note || "Split on the agency fee · details agreed in chat."}
        </div>
      )}
    </div>
  );
};

// Track record card (desktop rail) — artboard format: bold scope word, then
// "· 14 posted · 11 verified". Each label stays its own text node for the
// runtime translator. `deals` is still tracked in the payload but no longer
// shown (Marin 2026-08). Self-hides without data.
const TrackRecordStats = ({ label, stats }) => {
  if (!stats) return null;
  return (
    <div className="text-[12.5px] text-[#334155]">
      <b>{label}</b>
      {" · "}
      {stats.posted ?? 0} posted{" · "}
      {stats.verified ?? 0} verified
    </div>
  );
};

const TrackRecordCard = ({ trustStats }) => {
  if (!trustStats || (!trustStats.agent && !trustStats.agency)) return null;
  return (
    <div className="bg-white border border-[#EEF2F7] rounded-[10px] p-3 flex flex-col gap-1.5">
      <CellLabel>Track record</CellLabel>
      <TrackRecordStats label="Agent" stats={trustStats.agent} />
      <TrackRecordStats label="Agency" stats={trustStats.agency} />
    </div>
  );
};

const AgentTenderPreviewModal = ({
  isOpen,
  onClose,
  tender,
  variant = "feed",
  isOwn = false,
  primaryAction = null,
  onToggleSave = null,
  saving = false,
}) => {
  // Artboard cut: <768 renders the bottom sheet, ≥768 the centred modal.
  const isSheet = useMediaQuery("(max-width: 767px)");

  // ESC closes the modal
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // Body scroll lock while open
  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen || !tender) return null;

  const isMine = variant === "mine";
  const isStaff = tender.tender_category === "staff";
  const title = isStaff
    ? formatStaffRole(tender.staff_role, tender.other_staff_position)
    : formatPositionValue(tender.position, tender.other_staff_position);
  const level =
    tender.recruiting_for === "youth"
      ? "Youth"
      : tender.recruiting_for
        ? "Professional"
        : null;
  const levelPillClass =
    tender.recruiting_for === "youth"
      ? "bg-[#E8F1FF] text-[#1B6FE0]"
      : "bg-[#F1F5F9] text-[#475569]";
  const countryText = [tender.country?.name, formatLeagueTier(tender.league_tier)]
    .filter(Boolean)
    .join(" · ");

  // The club is only ever resolved on the agent's own internal view.
  const club = isMine ? tender.club : null;

  // Spec grid: every filled form field, empty cells auto-hide. Player and
  // staff share the Period/Level cells; the rest is category-specific.
  const languages =
    Array.isArray(tender.language_skills) && tender.language_skills.length > 0
      ? tender.language_skills.join(", ")
      : null;
  // Benefits: name the specific perks (travel / housing / food) when set,
  // else the plain "Offered" flag. All optional.
  const benefitsList = [
    tender.travel && "Travel",
    tender.housing && "Housing",
    tender.food_covered && "Food",
  ].filter(Boolean);
  const benefitsValue = benefitsList.length
    ? benefitsList.join(", ")
    : tender.offering_additional_benefits
      ? "Offered"
      : null;
  const trialPeriod =
    tender.type_of_tender === "trial"
      ? formatWindowDateRange(
          tender.trial_period_start_date,
          tender.trial_period_end_date,
        )
      : null;
  const specCells = [
    { label: "Period", value: getPeriodLine(tender) },
    ...(isStaff
      ? [
          { label: "Experience", value: getExperienceLine(tender) },
          { label: "Contract duration", value: formatDurationChip(tender) },
        ]
      : [
          { label: "Age range", value: formatRange(tender.min_age, tender.max_age) },
          {
            label: "Preferred foot",
            value:
              tender.foot === "L" ? "Left" : tender.foot === "R" ? "Right" : null,
          },
          {
            label: "Minimum height",
            value: formatHeightMeters(tender.minimum_height),
          },
          { label: "EU players", value: tender.only_eu_player ? "EU passport only" : null },
          { label: "Free agents only", value: tender.only_free_players ? "Yes" : null },
          { label: "Market value", value: getMarketValueLine(tender) },
          { label: "Games played", value: getGamesPlayedLine(tender) },
          {
            label: "Option to buy",
            value:
              tender.type_of_tender === "loan" && tender.loan_with_option_to_buy
                ? "Yes"
                : null,
          },
          { label: "Trial period", value: trialPeriod },
        ]),
    { label: "Languages", value: languages },
    { label: "Benefits", value: benefitsValue },
    { label: "Level", value: level },
  ];

  // Shared fragments ────────────────────────────────────────────────────

  const typePill = tender.type_of_tender && (
    <span
      className={`text-[11px] font-bold px-[9px] py-[3px] rounded-full capitalize ${typePillClass(
        tender.type_of_tender,
      )}`}
    >
      {formatTypeOfTender(tender.type_of_tender)}
    </span>
  );

  // Poster identity (feed) / own club (mine). `avatarSize` per artboard:
  // 40px on the desktop rail, 30px in the sheet's agency line.
  const posterIdentity = (avatarSize, ringClass, subline) =>
    isMine && club && (club.logo || club.name) ? (
      <div className="flex items-center gap-2.5">
        {club.logo && (
          <img
            src={club.logo}
            alt={club.name || ""}
            className="w-10 h-10 object-contain rounded-full"
            referrerPolicy="no-referrer"
          />
        )}
        {club.name && (
          <div className="text-[13.5px] font-bold text-[#0F172A] truncate">
            {club.name}
          </div>
        )}
      </div>
    ) : (
      !isMine && (
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="flex-none inline-flex items-center">
            {tender.posted_agency?.logo_url ? (
              <img
                src={tender.posted_agency.logo_url}
                alt={tender.posted_agency?.name || "Agency"}
                className={`object-contain bg-white border border-[#E2E8F0] ${
                  avatarSize === 40 ? "w-10 h-10 rounded-[11px]" : "w-[30px] h-[30px] rounded-[9px]"
                }`}
                referrerPolicy="no-referrer"
              />
            ) : (
              <ImageOrInitials
                src={null}
                alt={tender.posted_agency?.name}
                size={avatarSize === 40 ? "large" : "small"}
              />
            )}
            {tender.responsible_agent && (
              <span className={`${avatarSize === 40 ? "-ml-2.5" : "-ml-2"} ring-2 ${ringClass} rounded-full flex-none`}>
                <Avatar
                  src={tender.responsible_agent.avatar}
                  fallbackText={tender.responsible_agent.display_name}
                  size={avatarSize}
                />
              </span>
            )}
          </span>
          <div className="min-w-0">
            <div className={`font-bold text-[#0F172A] truncate ${avatarSize === 40 ? "text-[13.5px]" : "text-[12.5px] font-semibold"}`}>
              {tender.posted_agency?.name}
            </div>
            {tender.responsible_agent && (
              <div className={`text-[#64748B] truncate ${avatarSize === 40 ? "text-[12px]" : "text-[11.5px]"}`}>
                Posted by{" "}
                {tender.responsible_agent.display_name}
                {subline}
              </div>
            )}
          </div>
        </div>
      )
    );

  const freeTextSections = (tender.description || tender.key_requirements) && (
    <div className="border-t border-[#F1F5F9] pt-3.5 flex flex-col gap-3">
      {tender.description && (
        <div>
          <CellLabel>Description</CellLabel>
          <div className="text-[13px] text-[#334155] leading-relaxed mt-1 whitespace-pre-wrap break-words">
            {tender.description}
          </div>
        </div>
      )}
      {tender.key_requirements && (
        <div>
          <CellLabel>Key requirements</CellLabel>
          <div className="text-[13px] text-[#334155] leading-relaxed mt-1 whitespace-pre-wrap break-words">
            {tender.key_requirements}
          </div>
        </div>
      )}
    </div>
  );

  // ── Mobile bottom sheet (<768, artboard "Mobile sheet 390") ───────────
  if (isSheet) {
    const agentStats = tender.trust_stats?.agent;
    const sheetSubline = agentStats && (
      <>
        {" · "}
        {agentStats.posted ?? 0} posted{" · "}
        {agentStats.verified ?? 0} verified
      </>
    );
    const sheet = (
      <div
        className="fixed inset-0 bg-[rgba(15,23,42,0.42)] z-50"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-label="Request preview"
      >
        <div
          className="absolute left-0 right-0 bottom-0 h-[86%] bg-white rounded-t-[18px] shadow-[0_-12px_40px_rgba(15,23,42,0.25)] flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Grab handle */}
          <div className="pt-2.5 flex justify-center flex-none">
            <span className="w-9 h-1 rounded-full bg-[#E2E8F0]" />
          </div>
          {/* Pill header + close */}
          <div className="flex items-center gap-1.5 px-4 py-3 border-b border-[#F1F5F9] flex-none">
            {typePill}
            <ClubVerifiedBadge status={tender.verification_status} />
            <span className="flex-1" />
            <button
              type="button"
              onClick={onClose}
              className="w-7 h-7 border border-[#E2E8F0] rounded-lg inline-flex items-center justify-center text-[#64748B]"
              aria-label="Close preview"
            >
              <CloseGlyph size={12} />
            </button>
          </div>
          {/* Scrollable content */}
          <div className="flex-1 min-h-0 overflow-y-auto p-4 flex flex-col gap-[13px]">
            {/* No `capitalize` on the title — it is display-ready already and
                for staff role "other" it is the agent's OWN typed text (T059). */}
            <div className="flex items-baseline gap-2 flex-wrap">
              <h2 className="text-[20px] font-extrabold text-[#0F172A] tracking-[-0.01em]">
                {title}
              </h2>
              {level && (
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${levelPillClass}`}>
                  {level}
                </span>
              )}
            </div>
            {countryText && (
              <div className="flex items-center gap-[7px] text-[13px] text-[#475569] font-medium -mt-[5px]">
                <FlagImg country={tender.country} className="w-[18px] h-[13px] rounded-[2.5px]" />
                {countryText}
              </div>
            )}
            <LoupeFigures tender={tender} compact />
            {/* Spec rows — the sheet's bordered label/value list */}
            <div className="flex flex-col divide-y divide-[#F1F5F9]">
              {specCells.map((c) => (
                <SpecRow key={c.label} label={c.label} value={c.value} />
              ))}
            </div>
            {freeTextSections}
            {/* Agency line (never the club on the feed surface) */}
            <div className="border-t border-[#F1F5F9] pt-3">
              {posterIdentity(30, "ring-white", sheetSubline)}
            </div>
          </div>
          {/* Sticky action bar — square bookmark + full-width CTA */}
          <div className="flex-none flex items-center gap-2.5 px-4 py-3 border-t border-[#EEF2F7] bg-white">
            {!isMine && !isOwn && onToggleSave && (
              <button
                type="button"
                disabled={saving}
                onClick={() => onToggleSave(tender)}
                className={`flex-none w-[46px] h-[46px] rounded-[11px] inline-flex items-center justify-center border ${
                  tender.is_saved
                    ? "border-[#BFDBFE] bg-[#E8F1FF] text-[#2283FF]"
                    : "border-[#E2E8F0] bg-white text-[#64748B]"
                } ${saving ? "opacity-50 cursor-not-allowed" : ""}`}
                aria-label={tender.is_saved ? "Unsave request" : "Save request"}
              >
                <BookmarkGlyph saved={!!tender.is_saved} size={16} />
              </button>
            )}
            {primaryAction ? (
              <div className="flex-1 [&>button]:h-[46px] [&>button]:py-0 [&>button]:rounded-[11px] [&>button]:text-[14px]">
                {primaryAction}
              </div>
            ) : isMine || isOwn ? (
              <div className="flex-1 h-[46px] rounded-[11px] bg-[#E8F1FF] text-[#1B6FE0] text-[13px] font-bold inline-flex items-center justify-center">
                Your request
              </div>
            ) : (
              <span className="flex-1" />
            )}
          </div>
        </div>
      </div>
    );
    return createPortal(sheet, document.body);
  }

  // ── Desktop modal (≥768, artboard "Desktop modal 1440") ───────────────
  const overlay = (
    <div
      className="fixed inset-0 bg-[rgba(15,23,42,0.42)] z-50 flex items-start justify-center p-4 pt-16"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Request preview"
    >
      <div
        className="bg-white w-full max-w-[960px] rounded-2xl shadow-[0_24px_64px_rgba(15,23,42,0.28)] max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header: name of the surface + type + trust, then close */}
        <div className="flex items-center gap-2.5 px-6 py-4 border-b border-[#EEF2F7] flex-shrink-0">
          <span className="text-[15px] font-bold text-[#0F172A]">
            Request preview
          </span>
          {typePill}
          <ClubVerifiedBadge status={tender.verification_status} />
          <span className="flex-1" />
          <button
            type="button"
            onClick={onClose}
            className="w-[30px] h-[30px] border border-[#E2E8F0] rounded-lg inline-flex items-center justify-center text-[#64748B] hover:bg-gray-50"
            aria-label="Close preview"
          >
            <CloseGlyph size={13} />
          </button>
        </div>

        {/* ── Body: main column + 300px right rail */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="grid grid-cols-[1fr_300px]">
            {/* Main column */}
            <div className="p-6 flex flex-col gap-4">
              {/* No `capitalize` on the title — see the sheet variant above. */}
              <div className="flex items-baseline gap-2.5 flex-wrap">
                <h2 className="text-2xl font-extrabold text-[#0F172A] tracking-[-0.02em]">
                  {title}
                </h2>
                {level && (
                  <span className={`text-[11.5px] font-semibold px-2.5 py-[3px] rounded-full ${levelPillClass}`}>
                    {level}
                  </span>
                )}
              </div>

              {countryText && (
                <div className="flex items-center gap-2 text-[13.5px] text-[#475569] font-medium -mt-2">
                  <FlagImg country={tender.country} className="w-5 h-3.5 rounded-[2.5px]" />
                  {countryText}
                </div>
              )}

              {/* Real figures + commission (budget band is dead in the UI) */}
              <LoupeFigures tender={tender} />

              {/* Every filled requirement field; empty cells auto-hide */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-5 gap-y-3.5">
                {specCells.map((c) => (
                  <SpecCell key={c.label} label={c.label} value={c.value} />
                ))}
              </div>

              {/* Free text in FULL — truncated on the card, complete here */}
              {freeTextSections}
            </div>

            {/* Right rail: poster identity + track record + actions */}
            <div className="bg-[#F8FAFC] border-l border-[#EEF2F7] p-[22px] flex flex-col gap-3.5">
              {posterIdentity(40, "ring-[#F8FAFC]", null)}

              {/* Track record — feed surface only. Self-hides without data. */}
              {!isMine && <TrackRecordCard trustStats={tender.trust_stats} />}

              <span className="flex-1" />

              {/* Actions: caller-provided primary; own request gets a marker */}
              {primaryAction ? (
                primaryAction
              ) : isMine || isOwn ? (
                <div className="w-full py-2.5 rounded-[10px] bg-[#E8F1FF] text-[#1B6FE0] text-[12.5px] font-bold text-center">
                  Your request
                </div>
              ) : null}
              {!isMine && !isOwn && onToggleSave && (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => onToggleSave(tender)}
                  className={`w-full py-2.5 border border-[#E2E8F0] bg-white text-[#334155] text-[13px] font-semibold rounded-[10px] hover:bg-gray-50 ${
                    saving ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {tender.is_saved ? "Unsave request" : "Save request"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
};

export default AgentTenderPreviewModal;
